import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import background from "../../../assets/images/bg-oauth.png";
import { showToast } from "../../../components/custom/custom-toast";
import IconEye from "../../../components/icons/icon-eye";
import IconLockDots from "../../../components/icons/icon-lock-dots";
import IconMail from "../../../components/icons/icon-mail";
import { useFetchData } from "../../../hooks/useFetch";
import { EmployeeResponse } from "../../../models/response/employee";
import { MODULE_ROUTE } from "../../../routers/module-router";
import { API_DEVICE_TOKENS_ROUTER, API_OAUTH_ROUTER } from "../../../services/api-config";
import CookieUtils from "../../../utils/cookie-utils";
import { getDeviceUUID } from "../../../utils/helpers";
import { VerifyOtpComponent } from "../component/verify-otp-component";

export const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);
  const [isVerifyForm, setIsVerifyForm] = useState(false);
  const [isLoadingVerify, setIsLoadingVerify] = useState(false);
  const [invalidLoginButton, setInvalidLoginButton] = useState(true);

  const [companyName, setCompanyName] = useState("");
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");

  const [errorCompanyNameVisible, setErrorCompanyNameVisible] = useState(false);
  const [errorUserNameVisible, setErrorUserNameVisible] = useState(false);
  const [errorPasswordVisible, setErrorPasswordVisible] = useState(false);
  const [errorCompanyName, setErrorCompanyName] = useState("");
  const [errorUserName, setErrorUserName] = useState("");
  const [errorPassword, setErrorPassword] = useState("");

  const { request: onLogin, baseResponse: responsesLogin, loading: isLoading } = useFetchData.Post();
  const { request: onLoginVerify, baseResponse: responsesLoginVerify } = useFetchData.Post<EmployeeResponse>();
  const { request: onDeviceTokens, baseResponse: resDeviceToken } = useFetchData.Post();

  useEffect(() => {
    onDeviceTokens({
      endPoint: API_DEVICE_TOKENS_ROUTER.API_REMOVE_DEVICE_TOKENS(),
      data: {
        device_id: getDeviceUUID(),
        platform: 1,
      },
    });
  }, []);

  const callApiLogin = () => {
    onLogin({
      endPoint: API_OAUTH_ROUTER.API_LOGIN,
      data: {
        identifier_name: companyName.toLocaleLowerCase(),
        username: userName.toLocaleLowerCase(),
        password: btoa(password),
      },
    });
  };

  useEffect(() => {
    setInvalidLoginButton(companyName.length < 2 || userName.length < 2 || password.length < 6);
  }, [companyName, userName, password]);

  const callApiLoginVerify = (otpCode: string) => {
    onLoginVerify({
      endPoint: API_OAUTH_ROUTER.VERIFY_OTP_LOGIN,
      data: {
        identifier_name: companyName.toLocaleLowerCase(),
        username: userName.toLocaleLowerCase(),
        password: btoa(password),
        device_uuid: getDeviceUUID(),
        otp: otpCode,
      },
    });
  };

  useEffect(() => {
    if (!responsesLogin) return;

    if (responsesLogin.status === 200) {
      setIsVerifyForm(true);
    } else {
      showToast({ message: responsesLogin.message, type: "warning" });
    }
  }, [responsesLogin]);

  useEffect(() => {
    setIsLoadingVerify(false);
    if (!responsesLoginVerify) return;

    if (responsesLoginVerify.status === 200) {
      const response = responsesLoginVerify.data;
      response.totp_qr_code = "";
      const { privilege_codes, ...userDataToStore } = response;
      CookieUtils.setCookie("user", JSON.stringify(userDataToStore));
      CookieUtils.setCookie("branch_id", JSON.stringify(response.branch_ids[0]));
      CookieUtils.setCookie("privilege_codes", JSON.stringify(response.privilege_codes));
      navigate(MODULE_ROUTE.MANAGEMENT_SERVICE);
    } else {
      showToast({ message: responsesLoginVerify.message, type: "warning" });
    }
  }, [responsesLoginVerify, navigate, dispatch]);

  const resetErrors = () => {
    setErrorCompanyNameVisible(false);
    setErrorUserNameVisible(false);
    setErrorPasswordVisible(false);
  };

  const handleLogin = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    resetErrors();
    if (!validateLoginPage()) {
      return;
    }
    callApiLogin();
  };

  const validateLoginPage = () => {
    let isLogin = true;
    if (companyName.length < 2) {
      setErrorCompanyNameVisible(true);
      setErrorCompanyName("Độ dài tối thiểu 2 kí tự");
      isLogin = false;
    }
    if (userName.length < 2) {
      setErrorUserNameVisible(true);
      setErrorUserName("Độ dài tối thiểu 2 kí tự");
      isLogin = false;
    }
    if (password.length < 6) {
      setErrorPasswordVisible(true);
      setErrorPassword("Độ dài tối thiểu 6 kí tự");
      isLogin = false;
    }
    return isLogin;
  };

  interface RenderComponentProps {
    labelText: string;
    type?: React.HTMLInputTypeAttribute;
    icon?: React.ReactNode;
    isPassword?: boolean;
    value: string;
    setValue: (value: string) => void;
    isShowError?: boolean;
    messageError?: string;
    id?: string;
    autoFocus?: boolean;
    maxLength?: number;
  }

  function renderComponent({
    labelText,
    type,
    icon,
    isPassword = false,
    value,
    setValue,
    isShowError,
    messageError,
    id,
    autoFocus,
    maxLength,
  }: RenderComponentProps) {
    return (
      <div>
        <label htmlFor={id} className="block mb-2 text-sm font-medium text-gray-900">
          {labelText}
        </label>
        <div className="relative">
          <span className="absolute top-1/2 -translate-y-1/2 start-4">{icon}</span>
          <input
            id={id}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            type={type}
            placeholder="Nhập thông tin"
            className={`form-input ps-10 placeholder:text-white-dark ${isPassword && "pr-10"}`}
            autoComplete={isPassword ? "new-password" : "off"}
            autoFocus={autoFocus}
            maxLength={maxLength}
          />
          {isPassword && (
            <button
              type="button"
              className="absolute top-1/2 -translate-y-1/2 end-4"
              onClick={() => setShowPassword(!showPassword)}
            >
              <IconEye isIconOff={showPassword} />
            </button>
          )}
        </div>
        {isShowError && <span className="text-sm text-red-500">{messageError}</span>}
      </div>
    );
  }

  function renderLoginForm() {
    return (
      <>
        <div className="mb-5">
          <h1 className="text-xl font-bold tracking-tight leading-tight text-gray-900 uppercase md:text-2xl">
            Đăng nhập
          </h1>
        </div>

        <form className="space-y-4" data-testid="login-form" onSubmit={handleLogin}>
          {renderComponent({
            labelText: "Tên thương hiệu",
            id: "company-name-login",
            type: "text",
            icon: <IconMail fill={false} />,
            value: companyName,
            setValue: (value) => {
              if (/^\s+$/.test(value)) {
                return;
              }
              const valueTrim = value.trim();
              setCompanyName(valueTrim);
              setErrorCompanyNameVisible(valueTrim.length < 2);
              setErrorCompanyName(valueTrim.length < 2 ? "Độ dài tối thiểu 2 kí tự" : "");
            },
            isShowError: errorCompanyNameVisible,
            messageError: errorCompanyName,
            autoFocus: true,
            maxLength: 100,
          })}
          {renderComponent({
            labelText: "Tên đăng nhập",
            id: "user-name-login",
            type: "text",
            icon: <IconMail fill={false} />,
            value: userName,
            setValue: (value) => {
              if (/^\s+$/.test(value)) {
                return;
              }
              const valueTrim = value.trim();
              setUserName(valueTrim);
              setErrorUserNameVisible(valueTrim.length < 2);
              setErrorUserName(valueTrim.length < 2 ? "Độ dài tối thiểu 2 kí tự" : "");
            },
            isShowError: errorUserNameVisible,
            messageError: errorUserName,
            maxLength: 50,
          })}
          {renderComponent({
            labelText: "Mật khẩu",
            id: "password-login",
            type: showPassword ? "text" : "password",
            icon: <IconLockDots fill={false} />,
            value: password,
            setValue: (value) => {
              if (/^\s+$/.test(value)) {
                return;
              }
              const valueTrim = value.trim();
              setPassword(valueTrim);
              setErrorPasswordVisible(valueTrim.length < 6 || valueTrim.length > 100);
              setErrorPassword(
                valueTrim.length < 6
                  ? "Độ dài tối thiểu 6 kí tự"
                  : valueTrim.length > 100
                  ? "Độ dài tối đa 100 kí tự"
                  : ""
              );
            },
            isPassword: true,
            isShowError: errorPasswordVisible,
            messageError: errorPassword,
          })}
          <button
            type="submit"
            className="btn btn-gradient w-full border-0 shadow-[0_10px_20px_-10px_rgba(67,97,238,0.44)]"
            disabled={isLoading || invalidLoginButton}
          >
            {isLoading && (
              <span className="inline-block w-5 h-5 align-middle rounded-full border-2 border-white animate-spin border-l-transparent ltr:mr-4 rtl:ml-4"></span>
            )}
            ĐĂNG NHẬP
          </button>
          <div className="flex justify-center items-center">
            <button
              type="button"
              className="text-sm font-medium text-primary-600 hover:underline text-primary"
              onClick={() => navigate(MODULE_ROUTE.FORGOT_PASSWORD)}
            >
              Quên mật khẩu?
            </button>
          </div>
        </form>
      </>
    );
  }

  return (
    <div
      className="flex flex-col justify-center items-center px-6 py-8 mx-auto h-screen bg-center bg-no-repeat bg-cover lg:py-0"
      style={{
        backgroundImage: `url(${background})`,
      }}
    >
      <div className="w-full bg-white rounded-lg shadow dark:border md:mt-0 sm:max-w-md xl:p-0">
        <div className="p-6 space-y-4 md:space-y-6">
          {isVerifyForm ? (
            <VerifyOtpComponent
              setValueOtp={callApiLoginVerify}
              isLoading={isLoadingVerify}
              backWardForm={() => setIsVerifyForm(false)}
            />
          ) : (
            renderLoginForm()
          )}
        </div>
      </div>
    </div>
  );
};
